import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneX, PhoneSlash, User,
  ArrowClockwise, Plus, Note, Clock, CheckCircle, Warning, ChartLineUp,
  Key,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const TYPE_ICON = {
  incoming: PhoneIncoming,
  outgoing: PhoneOutgoing,
  missed: PhoneX,
  rejected: PhoneSlash,
  voicemail: Phone,
};
const TYPE_COLOR = {
  incoming: "text-[#1A3317] bg-[#D4E0D1]",
  outgoing: "text-[#0B2B66] bg-[#E4EAF5]",
  missed: "text-[#7A2E18] bg-[#F4C7B9]",
  rejected: "text-[#5C5A55] bg-[#EEEBE2]",
  voicemail: "text-[#7A5A11] bg-[#F9E4B7]",
};

const fmt = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(v); }
};
const fmtDuration = (s) => {
  if (!s) return "—";
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

export default function PhoneCRM() {
  const [summary, setSummary] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | incoming | outgoing | missed | unmatched
  const [logOpen, setLogOpen] = useState(false);
  const [outcomeFor, setOutcomeFor] = useState(null);
  const [autoTicketEnabled, setAutoTicketEnabled] = useState(true);
  const [settingsBusy, setSettingsBusy] = useState(false);

  useEffect(() => {
    api.get("/crm/phone/settings")
      .then(r => setAutoTicketEnabled(!!r.data?.auto_ticket_enabled))
      .catch(() => {});
  }, []);

  const toggleAutoTicket = async () => {
    setSettingsBusy(true);
    try {
      const next = !autoTicketEnabled;
      await api.put("/crm/phone/settings", { enabled: next });
      setAutoTicketEnabled(next);
      toast.success(`Missed-call auto-ticket ${next ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSettingsBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/crm/phone/summary"),
        api.get(`/crm/phone/calls?${filter === "unmatched"
          ? "unmatched_only=true"
          : (filter !== "all" ? `type=${filter}` : "")}&limit=200`),
      ]);
      setSummary(s.data);
      setCalls(c.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  return (
    <AppShell
      title="Phone CRM"
      subtitle="Call log · missed / attended / callback tracking"
      actions={
        <div className="flex gap-2 flex-wrap">
          <button onClick={toggleAutoTicket} disabled={settingsBusy}
                  data-testid="phone-autoticket-toggle"
                  title={autoTicketEnabled
                    ? "Missed calls auto-open a CRM ticket — click to disable"
                    : "Auto-ticket OFF — enable to auto-open a ticket on every missed call"}
                  className={`h-10 px-3 rounded-md text-[11px] font-semibold border inline-flex items-center gap-1.5 ${
                    autoTicketEnabled
                      ? "bg-[#0B2B66] text-white border-[#0B2B66]"
                      : "bg-white text-[#5C5A55] border-[#E5E2D9]"
                  }`}>
            <Warning size={11}/>Auto-ticket: {autoTicketEnabled ? "ON" : "OFF"}
          </button>
          <Link to="/crm/phone/report" data-testid="phone-report-link"
                className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <ChartLineUp size={12}/>Analytics
          </Link>
          <Link to="/crm/phone/passcodes" data-testid="phone-passcodes-link"
                className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <Key size={12}/>Passcodes
          </Link>
          <button onClick={load} data-testid="phone-refresh"
                  className="h-10 px-3 border border-[#E5E2D9] rounded-md text-xs inline-flex items-center gap-1.5 hover:bg-[#F7F6F2]">
            <ArrowClockwise size={12}/>Refresh
          </button>
          <button onClick={() => setLogOpen(true)} data-testid="phone-log-open"
                  className="h-10 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-xs inline-flex items-center gap-1.5">
            <Plus size={12} weight="bold"/>Log a call
          </button>
        </div>
      }
    >
      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mb-3"
             data-testid="phone-summary">
          <Chip label="Total calls" value={summary.total_period} testid="ph-total"/>
          <Chip label="Incoming" value={summary.incoming} tone="green" testid="ph-in"/>
          <Chip label="Attended" value={summary.attended} tone="green" testid="ph-attended"/>
          <Chip label="Missed" value={summary.missed} tone="red" testid="ph-missed"/>
          <Chip label="Callback due" value={summary.pending_callback} tone="amber" testid="ph-callback"/>
          <Chip label="Unmatched" value={summary.unmatched} tone="amber" testid="ph-unmatched"/>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-1.5 flex-wrap mb-3" data-testid="phone-filters">
        {[
          { v: "all", l: "All" },
          { v: "incoming", l: "Incoming" },
          { v: "outgoing", l: "Outgoing" },
          { v: "missed", l: "Missed" },
          { v: "unmatched", l: "Unmatched" },
        ].map(f => (
          <button key={f.v} onClick={() => setFilter(f.v)}
                  data-testid={`phone-filter-${f.v}`}
                  className={`h-8 px-3 rounded-full text-[11px] font-medium border ${
                    filter === f.v
                      ? "bg-[#2D5A27] border-[#2D5A27] text-white"
                      : "bg-white border-[#E5E2D9] hover:bg-[#F7F6F2]"
                  }`}>{f.l}</button>
        ))}
      </div>

      {/* Call list */}
      {loading && <div className="text-sm text-[#8A8780]">Loading calls…</div>}
      {!loading && calls.length === 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-md p-6"
             data-testid="phone-empty">
          <div className="text-center">
            <Phone size={32} className="mx-auto text-[#8A8780]"/>
            <div className="font-heading text-lg mt-2">No calls yet</div>
            <div className="text-sm text-[#5C5A55] mt-1 mb-5">
              To auto-sync your phone&apos;s call log, follow the 3 steps below.
              Or use <button onClick={() => setLogOpen(true)}
                              className="text-[#DC2626] font-semibold underline">
                Log a call
              </button> to add one manually.
            </div>
          </div>

          <ol className="mt-4 space-y-3 max-w-lg mx-auto text-left">
            <li className="flex gap-3" data-testid="phone-setup-step-1">
              <div className="w-7 h-7 shrink-0 rounded-full bg-[#DC2626] text-white flex items-center justify-center font-bold text-xs">1</div>
              <div className="text-sm">
                <div className="font-semibold text-[#111827]">Set a 6-digit passcode</div>
                <div className="text-[12px] text-[#5C5A55]">Go to <Link to="/crm/phone/passcodes" className="text-[#DC2626] underline">Passcodes</Link> and give the operator a passcode. Share the phone/email + passcode with them securely.</div>
              </div>
            </li>
            <li className="flex gap-3" data-testid="phone-setup-step-2">
              <div className="w-7 h-7 shrink-0 rounded-full bg-[#DC2626] text-white flex items-center justify-center font-bold text-xs">2</div>
              <div className="text-sm">
                <div className="font-semibold text-[#111827]">Install the Rescigo Phone Android app</div>
                <div className="text-[12px] text-[#5C5A55]">
                  Grab the latest APK from GitHub Actions →{" "}
                  <a href="https://github.com/rescigo/rescigo-master-panel/actions/workflows/companion-apk.yml"
                      target="_blank" rel="noopener noreferrer"
                      className="text-[#DC2626] underline">
                    companion-apk workflow
                  </a>. Tap the latest successful run → Artifacts → <code className="bg-[#F7F6F2] px-1 rounded">rescigo-phone-debug-apk</code>. Side-load on any Android 8+ phone.
                </div>
              </div>
            </li>
            <li className="flex gap-3" data-testid="phone-setup-step-3">
              <div className="w-7 h-7 shrink-0 rounded-full bg-[#DC2626] text-white flex items-center justify-center font-bold text-xs">3</div>
              <div className="text-sm">
                <div className="font-semibold text-[#111827]">Sign in &amp; grant Call Log permission</div>
                <div className="text-[12px] text-[#5C5A55]">Open the app → enter phone/email + passcode → tap <span className="font-semibold">Sync now</span> → Allow <span className="font-mono bg-[#F7F6F2] px-1 rounded">READ_CALL_LOG</span>. Calls will sync automatically every 2 minutes.</div>
              </div>
            </li>
          </ol>

          <div className="mt-5 mx-auto max-w-lg p-3 rounded-md bg-[#FEF3C7] border border-[#FDE68A] text-[11px] text-[#78350F]">
            <span className="font-bold">iOS note:</span> Apple does not permit third-party apps to read the call log. This companion is Android-only.
          </div>
        </div>
      )}
      {!loading && calls.length > 0 && (
        <div className="space-y-1.5" data-testid="phone-list">
          {calls.map((c, idx) => {
            const cid = c.id || c.dedup_key || `${c.phone_normalized}-${idx}`;
            const Icon = TYPE_ICON[c.type] || Phone;
            const toneClass = TYPE_COLOR[c.type] || "text-[#5C5A55] bg-[#F7F6F2]";
            return (
              <div key={cid}
                    data-testid={`phone-row-${cid}`}
                    className="bg-white border border-[#E5E2D9] rounded-md p-3 flex items-start gap-3">
                <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 ${toneClass}`}>
                  <Icon size={16} weight={c.type === "missed" ? "fill" : "regular"}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center flex-wrap gap-1.5">
                    {c.customer_name ? (
                      <span className="font-heading text-sm font-medium truncate"
                            data-testid={`phone-row-name-${cid}`}>
                        <User size={11} className="inline mr-1 text-[#2D5A27]"/>
                        {c.customer_name}
                      </span>
                    ) : (
                      <span className="font-mono text-sm text-[#5C5A55]"
                            data-testid={`phone-row-name-${cid}`}>
                        {c.phone_raw || c.phone_normalized}
                      </span>
                    )}
                    {c.customer_code && (
                      <span className="text-[10px] font-mono text-[#8A8780]">
                        · {c.customer_code}
                      </span>
                    )}
                    <span className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded font-semibold ${toneClass}`}>
                      {c.type}
                    </span>
                    {c.outcome && (
                      <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded font-semibold bg-[#E4EAF5] text-[#0B2B66]"
                            data-testid={`phone-row-outcome-${cid}`}>
                        {c.outcome.replace(/_/g, " ")}
                      </span>
                    )}
                    {c.auto_ticket_id && (
                      <a href={`/crm/tickets?open=${c.auto_ticket_id}`}
                          data-testid={`phone-row-ticket-${cid}`}
                          className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded font-semibold bg-[#F9E4B7] text-[#7A5A11] hover:bg-[#F4CF82] inline-flex items-center gap-0.5">
                        <Warning size={10}/>Ticket open
                      </a>
                    )}
                  </div>
                  <div className="text-[11px] text-[#5C5A55] mt-0.5 flex flex-wrap gap-2">
                    <span className="font-mono">{c.phone_raw || c.phone_normalized}</span>
                    <span className="inline-flex items-center gap-0.5">
                      <Clock size={10}/>{fmt(c.started_at)}
                    </span>
                    <span>{fmtDuration(c.duration_sec)}</span>
                    {c.staff_name && (
                      <span className="text-[10px] text-[#8A8780]">via {c.staff_name}</span>
                    )}
                  </div>
                  {c.notes && (
                    <div className="text-[11px] text-[#3A3A38] mt-1 bg-[#F7F6F2] rounded px-2 py-1">
                      <Note size={10} className="inline mr-1"/>{c.notes}
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
                  <a href={`tel:${c.phone_raw || c.phone_normalized}`}
                     data-testid={`phone-row-callback-${cid}`}
                     className="h-8 px-2.5 border border-[#2D5A27] text-[#2D5A27] rounded text-[11px] inline-flex items-center gap-1 hover:bg-[#EFF5EC]">
                    <PhoneOutgoing size={11}/>Call
                  </a>
                  <button onClick={() => setOutcomeFor(c)}
                          data-testid={`phone-row-outcome-btn-${cid}`}
                          className="h-8 px-2.5 border border-[#E5E2D9] text-[#5C5A55] rounded text-[11px] inline-flex items-center gap-1 hover:bg-[#F7F6F2]">
                    <CheckCircle size={11}/>Outcome
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {logOpen && <LogCallModal onClose={() => setLogOpen(false)} onSaved={() => { setLogOpen(false); load(); }}/>}
      {outcomeFor && <OutcomeModal call={outcomeFor} onClose={() => setOutcomeFor(null)} onSaved={() => { setOutcomeFor(null); load(); }}/>}
    </AppShell>
  );
}

function Chip({ label, value, tone = "default", testid }) {
  const bg = tone === "green" ? "bg-[#E8F0E6] text-[#1A3317]"
          : tone === "amber" ? "bg-[#F9E4B7] text-[#7A5A11]"
          : tone === "red" ? "bg-[#F4C7B9] text-[#7A2E18]"
          : "bg-[#F7F6F2] text-[#3A3A38]";
  return (
    <div className={`${bg} border border-[#E5E2D9] rounded-md px-3 py-1.5`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] opacity-70">{label}</div>
      <div className="font-heading text-lg font-semibold mt-0.5">{value ?? "—"}</div>
    </div>
  );
}

function LogCallModal({ onClose, onSaved }) {
  const [number, setNumber] = useState("");
  const [type, setType] = useState("outgoing");
  const [outcome, setOutcome] = useState("attended");
  const [dur, setDur] = useState("0");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!number.trim()) { toast.error("Number required"); return; }
    setBusy(true);
    try {
      await api.post("/crm/phone/log", {
        number: number.trim(), type, outcome,
        duration_sec: Number(dur || 0), notes: notes || null,
      });
      toast.success("Call logged");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="phone-log-modal">
      <div className="bg-white w-full max-w-md rounded-md p-5 space-y-3"
           onClick={e => e.stopPropagation()}>
        <div className="font-heading text-lg">Log a call</div>
        <input placeholder="Phone number" value={number}
                onChange={e => setNumber(e.target.value)} inputMode="tel"
                data-testid="phone-log-number"
                className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
        <div className="grid grid-cols-2 gap-2">
          <select value={type} onChange={e => setType(e.target.value)}
                  data-testid="phone-log-type"
                  className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
            <option value="outgoing">Outgoing</option>
            <option value="incoming">Incoming</option>
            <option value="missed">Missed</option>
          </select>
          <select value={outcome} onChange={e => setOutcome(e.target.value)}
                  data-testid="phone-log-outcome"
                  className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
            <option value="attended">Attended</option>
            <option value="no_answer">No answer</option>
            <option value="callback_needed">Callback needed</option>
            <option value="voicemail">Voicemail</option>
          </select>
        </div>
        <input type="number" min="0" value={dur} onChange={e => setDur(e.target.value)}
                placeholder="Duration (seconds)"
                data-testid="phone-log-duration"
                className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                    placeholder="Notes (optional)"
                    data-testid="phone-log-notes"
                    className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} data-testid="phone-log-cancel"
                  className="flex-1 h-10 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="phone-log-save"
                  className="flex-1 h-10 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function OutcomeModal({ call, onClose, onSaved }) {
  const [outcome, setOutcome] = useState(call.outcome || "attended");
  const [notes, setNotes] = useState(call.notes || "");
  const [busy, setBusy] = useState(false);
  const cid = call.id || call.dedup_key;
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/crm/phone/calls/${encodeURIComponent(cid)}`,
                        { outcome, notes: notes || null });
      toast.success("Outcome saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="phone-outcome-modal">
      <div className="bg-white w-full max-w-md rounded-md p-5 space-y-3"
           onClick={e => e.stopPropagation()}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">Log outcome</div>
          <div className="font-heading text-lg">
            {call.customer_name || call.phone_raw || call.phone_normalized}
          </div>
        </div>
        <select value={outcome} onChange={e => setOutcome(e.target.value)}
                data-testid="phone-outcome-select"
                className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
          <option value="attended">Attended</option>
          <option value="no_answer">No answer</option>
          <option value="callback_needed">Callback needed</option>
          <option value="resolved">Resolved</option>
          <option value="voicemail">Voicemail</option>
        </select>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                    placeholder="Notes…"
                    data-testid="phone-outcome-notes"
                    className="w-full px-3 py-2 border border-[#E5E2D9] rounded-md text-sm"/>
        <div className="flex gap-2">
          <button onClick={onClose} data-testid="phone-outcome-cancel"
                  className="flex-1 h-10 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="phone-outcome-save"
                  className="flex-1 h-10 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
